import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, TrendingUp, TrendingDown, Minus, Plus, Calendar, User, MoreVertical, Trash2, CheckCircle2, Sparkles, Edit2 } from 'lucide-react';
import { useKPIs, KPI } from '../contexts/KPIContext';
import { KPITimelineChart } from './KPITimelineChart';
export function KPIsTab() {
  const {
    kpis,
    removeKPI,
    addKPI,
    updateKPI
  } = useKPIs();
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'leading' | 'lagging'>('all');
  const [editingKPI, setEditingKPI] = useState<string | null>(null);
  // Form state
  const [title, setTitle] = useState('');
  const [metric, setMetric] = useState('');
  const [type, setType] = useState<'leading' | 'lagging'>('leading');
  const [baseline, setBaseline] = useState('');
  const [target, setTarget] = useState('');
  const [unit, setUnit] = useState('%');
  const [owner, setOwner] = useState('');
  const [date, setDate] = useState('');
  const filteredKPIs = kpis.filter(k => filterType === 'all' || k.type === filterType);
  const stats = {
    on_track: filteredKPIs.filter(k => k.status === 'on_track').length,
    at_risk: filteredKPIs.filter(k => k.status === 'at_risk').length,
    behind: filteredKPIs.filter(k => k.status === 'behind').length,
    achieved: filteredKPIs.filter(k => k.status === 'achieved').length
  };
  const getStatusConfig = (status: KPI['status']) => {
    switch (status) {
      case 'on_track':
        return {
          label: 'On Track',
          color: 'text-emerald-700',
          bg: 'bg-emerald-100',
          border: 'border-emerald-200',
          icon: TrendingUp
        };
      case 'at_risk':
        return {
          label: 'At Risk',
          color: 'text-amber-700',
          bg: 'bg-amber-100',
          border: 'border-amber-200',
          icon: Minus
        };
      case 'behind':
        return {
          label: 'Behind',
          color: 'text-red-700',
          bg: 'bg-red-100',
          border: 'border-red-200',
          icon: TrendingDown
        };
      case 'achieved':
        return {
          label: 'Achieved',
          color: 'text-blue-700',
          bg: 'bg-blue-100',
          border: 'border-blue-200',
          icon: CheckCircle2
        };
    }
  };
  const handleEditClick = (kpi: KPI) => {
    setTitle(kpi.title);
    setMetric(kpi.metric);
    setType(kpi.type);
    setBaseline(kpi.baselineValue.toString());
    setTarget(kpi.targetValue.toString());
    setUnit(kpi.unit);
    setOwner(kpi.owner);
    setDate(kpi.targetDate);
    setEditingKPI(kpi.id);
    setShowAddForm(true);
    setActiveMenu(null);
  };
  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingKPI(null);
    setTitle('');
    setMetric('');
    setType('leading');
    setBaseline('');
    setTarget('');
    setUnit('%');
    setOwner('');
    setDate('');
  };
  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !metric || !baseline || !target || !owner || !date) return;
    if (editingKPI) {
      updateKPI(editingKPI, {
        title,
        metric,
        type,
        baselineValue: Number(baseline),
        targetValue: Number(target),
        unit,
        targetDate: date,
        owner
      });
    } else {
      addKPI({
        title,
        metric,
        type,
        baselineValue: Number(baseline),
        currentValue: Number(baseline),
        targetValue: Number(target),
        unit,
        targetDate: date,
        status: 'on_track',
        owner
      });
    }
    handleCancelForm();
  };
  // Simple SVG Sparkline
  const Sparkline = ({
    data,
    color
  }: {
    data: number[];
    color: string;
  }) => {
    if (data.length < 2) return null;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const width = 60;
    const height = 20;
    const points = data.map((val, i) => {
      const x = i / (data.length - 1) * width;
      const y = height - (val - min) / range * height;
      return `${x},${y}`;
    }).join(' ');
    return <svg width={width} height={height} className="overflow-visible" data-id="element-852" data-mp="2j3ejky0e40xu">
        <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-853" data-mp="2e6o6mjjvtlgn" />
      </svg>;
  };
  return <div className="flex flex-col h-full" data-id="element-854" data-mp="24eidh6hw8xzv">
      <KPITimelineChart data-id="element-855" data-mp="1zgypvfzb38v9" />

      {/* Header & Stats */}
      <div className="mb-4 space-y-3" data-id="element-856" data-mp="1zhi5gq1hhl64">
        <div className="flex items-center justify-between" data-id="element-857" data-mp="1vfdgw5pw7eqc">
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg" data-id="element-858" data-mp="1vgyb1u8aovno">
            <button onClick={() => setFilterType('all')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filterType === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-859" data-mp="1qf8xa6tou3vw">
              All KPIs
            </button>
            <button onClick={() => setFilterType('leading')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filterType === 'leading' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-860" data-mp="5wooirr288p1">
              Leading (Proactive)
            </button>
            <button onClick={() => setFilterType('lagging')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filterType === 'lagging' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-861" data-mp="3jd5sxjebpkw3">
              Lagging (Outcomes)
            </button>
          </div>
          <button onClick={() => {
          handleCancelForm();
          setShowAddForm(true);
        }} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg text-sm font-medium transition-colors" data-id="element-862" data-mp="2vmsamkljj0ep">
            <Plus size={16} data-id="element-863" data-mp="1usnsa30ay0t" />
            Add KPI
          </button>
        </div>

        <div className="flex flex-wrap gap-2" data-id="element-864" data-mp="gkrig2vcrrbj">
          {stats.on_track > 0 && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-100 rounded-full text-xs font-medium text-emerald-700" data-id="element-865" data-mp="bkvdo1etzvux">
              <span className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-866" data-mp="3k3b0kckbhgsm" />
              {stats.on_track} On Track
            </div>}
          {stats.at_risk > 0 && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-100 rounded-full text-xs font-medium text-amber-700" data-id="element-867" data-mp="3t2oepijdytjo">
              <span className="w-2 h-2 rounded-full bg-amber-500" data-id="element-868" data-mp="18be27712n8ow" />
              {stats.at_risk} At Risk
            </div>}
          {stats.behind > 0 && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 border border-red-100 rounded-full text-xs font-medium text-red-700" data-id="element-869" data-mp="14c5as8ti7mwi">
              <span className="w-2 h-2 rounded-full bg-red-500" data-id="element-870" data-mp="1gsy9bx08xisa" />
              {stats.behind} Behind
            </div>}
          {stats.achieved > 0 && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-100 rounded-full text-xs font-medium text-blue-700" data-id="element-871" data-mp="11xrz0xu4axt5">
              <span className="w-2 h-2 rounded-full bg-blue-500" data-id="element-872" data-mp="1ul4gitlf8ffl" />
              {stats.achieved} Achieved
            </div>}
        </div>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence data-id="element-873" data-mp="1kv1jmib6ihbz">
        {showAddForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden mb-4" data-id="element-874" data-mp="1ksff6rjl4hxz">
            <form onSubmit={handleAddSubmit} className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4" data-id="element-875" data-mp="i8fy2d8pgihj">
              <div className="flex items-center justify-between mb-2" data-id="element-876" data-mp="9f3zxqvp3w15">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-877" data-mp="9fcbnfg30var">
                  <Target size={16} className="text-indigo-600" data-id="element-878" data-mp="4hklapv35h8o" />
                  {editingKPI ? 'Edit KPI' : 'New KPI'}
                </h3>
                <button type="button" onClick={handleCancelForm} className="text-gray-400 hover:text-gray-600 text-sm" data-id="element-879" data-mp="x4oul717ohq9">
                  Cancel
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3" data-id="element-880" data-mp="n5z9z6hxkj6b">
                <div className="col-span-2" data-id="element-881" data-mp="3cwcg86aryntr">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-882" data-mp="3cx4bth4dglk1">
                    KPI Title
                  </label>
                  <input type="text" required value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Reduce Fall Hazards" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-883" data-mp="2xt1j4fez7q7y" />
                </div>
                <div data-id="element-884" data-mp="3gjsnt3o0qsg0">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-885" data-mp="3gi99vjpu4the">
                    Metric Name
                  </label>
                  <input type="text" required value={metric} onChange={e => setMetric(e.target.value)} placeholder="e.g., Fall Incidents" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-886" data-mp="3lkgn0cmeiflb" />
                </div>
                <div data-id="element-887" data-mp="214dd6hgjbtew">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-888" data-mp="3892f78gz972a">
                    Type
                  </label>
                  <select value={type} onChange={e => setType(e.target.value as 'leading' | 'lagging')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-889" data-mp="3d8f2tvfoai5x">
                    <option value="leading" data-id="element-890" data-mp="3r5keqc22ib93">Leading (Proactive)</option>
                    <option value="lagging" data-id="element-891" data-mp="3w4x2cz0rjmcq">Lagging (Outcome)</option>
                  </select>
                </div>
                <div data-id="element-892" data-mp="4twzg70ofqsn">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-893" data-mp="8te3qx4dahw8">
                    Unit
                  </label>
                  <select value={unit} onChange={e => setUnit(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-894" data-mp="dtjr413lti8i">
                    <option value="%" data-id="element-895" data-mp="nwp18p42vhik">%</option>
                    <option value="hrs" data-id="element-896" data-mp="nuuf57h2ljcw">Hours</option>
                    <option value="count" data-id="element-897" data-mp="ix2n8dgfdx8p">Count</option>
                    <option value="pts" data-id="element-898" data-mp="ixm8a31dh1qy">Points</option>
                    <option value="rate" data-id="element-899" data-mp="ew93lwa0n4lm">Rate</option>
                  </select>
                </div>
                <div data-id="element-900" data-mp="1bm9lrcfvqmty">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-901" data-mp="1bkosdq5ax9xo">
                    Baseline Value
                  </label>
                  <input type="number" step="any" required value={baseline} onChange={e => setBaseline(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-902" data-mp="16p9i186i5w60" />
                </div>
                <div data-id="element-903" data-mp="1pjoiz3cdjywh">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-904" data-mp="3jtwngvildxam">
                    Target Value
                  </label>
                  <input type="number" step="any" required value={target} onChange={e => setTarget(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-905" data-mp="3oreolzqdb882" />
                </div>
                <div data-id="element-906" data-mp="3639n6yx6nzcf">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-907" data-mp="363t6aiykkts1">
                    Owner
                  </label>
                  <input type="text" required value={owner} onChange={e => setOwner(e.target.value)} placeholder="e.g., Safety Team" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-908" data-mp="2w42c6idx8gsh" />
                </div>
                <div data-id="element-909" data-mp="29vn6pa1vj7zr">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-910" data-mp="29w578ivkk8dh">
                    Target Date
                  </label>
                  <input type="date" required value={date} onChange={e => setDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-911" data-mp="2oludutmfl4l5" />
                </div>
              </div>

              <div className="pt-2" data-id="element-912" data-mp="37icdrybfoash">
                <button type="submit" className="w-full py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-913" data-mp="37im2likqsbwl">
                  {editingKPI ? 'Save Changes' : 'Create KPI'}
                </button>
              </div>
            </form>
          </motion.div>}
      </AnimatePresence>

      {/* KPI List */}
      <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-3 pb-4" data-id="element-914" data-mp="19pt388licj3g">
        {filteredKPIs.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-915" data-mp="1jlmn6d1k3ev9">
            <Target size={32} className="mx-auto text-gray-400 mb-3" data-id="element-916" data-mp="1okfrpfyv7vja" />
            <h3 className="text-sm font-medium text-gray-900" data-id="element-917" data-mp="1oimt8nyeohmd">
              No KPIs tracked
            </h3>
            <p className="text-xs text-gray-500 mt-1" data-id="element-918" data-mp="qv4feypw0pbi">
              Set goals to track your safety improvements.
            </p>
          </div> : filteredKPIs.map((kpi, index) => {
        const statusConf = getStatusConfig(kpi.status);
        const StatusIcon = statusConf.icon;
        // Calculate progress percentage for the bar
        const totalRange = Math.abs(kpi.targetValue - kpi.baselineValue) || 1;
        const currentProgress = Math.abs(kpi.currentValue - kpi.baselineValue);
        const progressPercent = Math.min(100, Math.max(0, currentProgress / totalRange * 100));
        // Sparkline color
        const sparkColor = kpi.status === 'on_track' || kpi.status === 'achieved' ? '#10b981' // emerald-500
        : kpi.status === 'at_risk' ? '#f59e0b' // amber-500
        : '#ef4444'; // red-500
        return <motion.div key={kpi.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-indigo-300 transition-colors relative group" data-id="element-919" data-mp="22p6nadpxvhf5" data-mpi={index}>
                {/* Header */}
                <div className="flex items-start justify-between mb-3" data-id="element-920" data-mp="1a2gdy6xg2mh5">
                  <div className="pr-8" data-id="element-921" data-mp="1f3vy7wrssoyh">
                    <div className="flex items-center gap-2 mb-1" data-id="element-922" data-mp="1f0ro6irniyzw">
                      <h3 className="font-bold text-gray-900" data-id="element-923" data-mp="114eg2090c9y3">{kpi.title}</h3>
                      {kpi.linkedInsightId && <span className="flex items-center gap-1 px-1.5 py-0.5 bg-indigo-50 text-indigo-600 text-[9px] font-bold uppercase tracking-wider rounded border border-indigo-100" data-id="element-924" data-mp="156rc1wxyctho">
                          <Sparkles size={10} data-id="element-925" data-mp="1xskeu6j9084g" />
                          From Insight
                        </span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-926" data-mp="1o2hf7nhmfvx5">
                      <span className="flex items-center gap-1" data-id="element-927" data-mp="2bslks0ede5qs">
                        <User size={12} data-id="element-928" data-mp="2bracbn9zc9tc" />
                        {kpi.owner}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-929" data-mp="54uxxyurbsdp">
                        <Calendar size={12} data-id="element-930" data-mp="539ypvxhzl9i" />
                        Due {new Date(kpi.targetDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-md border ${statusConf.bg} ${statusConf.color} ${statusConf.border}`} data-id="element-931" data-mp="9vl4vgiu03ai">
                    <StatusIcon size={14} data-id="element-932" data-mp="3id7pb2we5kvv" />
                    <span className="text-xs font-semibold" data-id="element-933" data-mp="3ddd1574039ei">
                      {statusConf.label}
                    </span>
                  </div>
                </div>

                {/* Progress Section */}
                <div className="mt-4" data-id="element-934" data-mp="3n9obhrk15p6b">
                  <div className="flex items-end justify-between mb-1.5" data-id="element-935" data-mp="3ncu0z7wkmt2k">
                    <div data-id="element-936" data-mp="17hifysuvt6cf">
                      <span className="text-xs font-medium text-gray-500" data-id="element-937" data-mp="12k8tnm2shast">
                        {kpi.metric}
                      </span>
                      <div className="flex items-baseline gap-1 mt-0.5" data-id="element-938" data-mp="2w40mofb3m6ar">
                        <span className="text-xl font-bold text-gray-900" data-id="element-939" data-mp="311izigs8l5o1">
                          {kpi.currentValue}
                          {kpi.unit}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-940" data-mp="3arvk2901wyu6">
                          / {kpi.targetValue}
                          {kpi.unit} target
                        </span>
                      </div>
                    </div>

                    {/* Sparkline */}
                    <div className="flex flex-col items-end" data-id="element-941" data-mp="3jumv47p0lsla">
                      <span className="text-[10px] text-gray-400 mb-1" data-id="element-942" data-mp="3osy5qkn4w74k">
                        Trend
                      </span>
                      <Sparkline data={kpi.history.map(h => h.value)} color={sparkColor} data-id="element-943" data-mp="3ton80vqjg24k" />
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden mt-2" data-id="element-944" data-mp="1rtfo8x7zl1i1">
                    <div className={`absolute top-0 bottom-0 left-0 rounded-full transition-all duration-500 ${kpi.status === 'behind' ? 'bg-red-500' : kpi.status === 'at_risk' ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{
                width: `${progressPercent}%`
              }} data-id="element-945" data-mp="1n27eu16npg4v" />
                  </div>
                  <div className="flex justify-between mt-1" data-id="element-946" data-mp="25sfgeeupjyiy">
                    <span className="text-[10px] text-gray-400" data-id="element-947" data-mp="25r4ao9hpcgp7">
                      Baseline: {kpi.baselineValue}
                      {kpi.unit}
                    </span>
                    <span className="text-[10px] text-gray-400" data-id="element-948" data-mp="1vu9gg2sgp2gq">
                      Target: {kpi.targetValue}
                      {kpi.unit}
                    </span>
                  </div>
                </div>

                {/* Context Menu */}
                <div className="absolute top-3 right-3" data-id="element-949" data-mp="3ge199iq777x3">
                  <button onClick={() => setActiveMenu(activeMenu === kpi.id ? null : kpi.id)} className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-950" data-mp="3gfm1h1p1iers">
                    <MoreVertical size={16} data-id="element-951" data-mp="36r263rdsjlv" />
                  </button>

                  <AnimatePresence data-id="element-952" data-mp="34wptdu6dzte">
                    {activeMenu === kpi.id && <motion.div initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 shadow-lg rounded-lg py-1 z-10" data-id="element-953" data-mp="3ue3jcis9kx1s">
                        <button onClick={() => handleEditClick(kpi)} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 text-left" data-id="element-954" data-mp="tex8k8mo5owu">
                          <Edit2 size={14} data-id="element-955" data-mp="186pb1k1d78h5" />
                          Edit KPI
                        </button>
                        <button onClick={() => {
                  removeKPI(kpi.id);
                  setActiveMenu(null);
                }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 text-left" data-id="element-956" data-mp="1d3xsfieov8hc">
                          <Trash2 size={14} data-id="element-957" data-mp="3nx247fmxnt8u" />
                          Delete KPI
                        </button>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </motion.div>;
      })}
      </div>
    </div>;
}